<template>
  <v-container class="fill-height" style="opacity: 0.94" fluid>
    <v-sheet elevation="1" rounded width="100%" class="mt-16 mb-8">
      <deprecation-banner />
      <v-row justify="space-between" class="pa-2">
        <v-col cols="12" md="6">
          <v-text-field
            :label="$t('Search')"
            prepend-icon="mdi-magnify"
            placeholder="e.g. 1234"
            @keyup.enter="searchTimeOut"
          ></v-text-field>
        </v-col>
        <v-col align="right">
          <v-btn-toggle v-if="selected.length === 0">
            <v-menu :close-on-content-click="false" offset-y :transition="false">
              <template #activator="{ on, attrs }">
                <v-btn large value="center" v-bind="attrs" v-on="on">
                  <v-icon>mdi-view-column-outline</v-icon>
                  <span class="hidden-sm-and-down">{{ $t('Columns') }}</span>
                </v-btn>
              </template>

              <v-card min-width="205px">
                <v-list>
                  <template v-for="item in headers">
                    <v-list-item
                      class="text-left"
                      link
                      v-if="(item.admin && isAdmin) || !item.admin"
                      :key="item.value"
                    >
                      <v-list-item-action>
                        <v-checkbox v-model="item.active" :disabled="item.fixed" />
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>{{ $t(item.text) }}</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-card>
            </v-menu>

            <v-menu :close-on-content-click="false" offset-y :transition="false">
              <template #activator="{ on, attrs }">
                <v-btn large value="right" v-bind="attrs" v-on="on">
                  <v-icon>mdi-swap-vertical</v-icon>
                  <span class="hidden-sm-and-down">{{ $t('Sort') }}</span>
                </v-btn>
              </template>

              <v-card min-width="205px">
                <v-card-text>
                  <v-subheader>Group By</v-subheader>
                  <v-radio-group v-model="groupedAs" column>
                    <v-radio :label="$t('None')" :value="null"></v-radio>
                    <v-radio :label="$t('Date')" value="calendarDate"></v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>
            </v-menu>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-data-table
        ref="dataTable"
        v-model="selected"
        :headers="activeHeaders"
        height="65vh"
        fixed-header
        :items="tableRows"
        item-key="id"
        :items-per-page="50"
        class="elevation-1"
        :loading="loading"
        show-select
        loading-text="Loading... Please wait"
        :group-by="groupedAs"
        hide-default-footer
      >
        <template v-slot:[`header.data-table-select`]="{ props, on }">
          <v-simple-checkbox
            :value="props.value || props.indeterminate"
            v-on="on"
            :indeterminate="props.indeterminate"
            color="secondary"
          />
        </template>
        <template v-slot:[`group.header`]="{ group, headers, toggle, isOpen }">
          <td :colspan="headers.length" class="text-left">
            <v-btn @click="toggle" x-small icon :ref="group">
              <v-icon v-if="isOpen">mdi-plus</v-icon>
              <v-icon v-else>mdi-minus</v-icon>
            </v-btn>
            <span class="mx-5 font-weight-bold">{{ group }}</span>
          </td>
        </template>
        <template v-slot:[`item.orderNumber`]="{ item }">
          <td class="fixed-col">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div>
                  <a
                    class="text-white text-decoration-none font-weight-bold"
                    style="color: lightgrey"
                    target="_blank"
                    :href="`${selectedStore.shopUrl}/orders/${item.id}`"
                    v-bind="attrs"
                    v-on="on"
                  >
                    # {{ item.orderNumber }}
                  </a>
                </div>
              </template>
              <span>
                {{ $t('Open in shopify') }}
                <v-icon small>mdi-open-in-new</v-icon>
              </span>
            </v-tooltip>
          </td>
        </template>

        <template v-slot:[`item.createdAt`]="{ item }">
          {{ item.createdAt | moment }}
        </template>

        <template v-slot:[`item.customer`]="{ item }">
          <v-menu :close-on-content-click="false" max-width="min-content" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-hover v-slot="{ hover }">
                <v-btn plain v-bind="attrs" v-on="on" class="text-none">
                  {{ `${item.customer.firstName} ${item.customer.lastName}` }}
                  <v-icon :style="`opacity:${hover ? 1 : 0}`" right> mdi-chevron-down </v-icon>
                </v-btn>
              </v-hover>
            </template>
            <customer-card
              :title="`${item.customer.firstName} ${item.customer.lastName}`"
              :subtitle="`${item.customer.city} ${item.customer.provinceCode}, ${item.customer.country}`"
              :description="item.customer.email"
              :third="item.customer.ordersCount"
              :action="`${selectedStore.shopUrl}/customers/${item.customer.id}`"
            ></customer-card>
          </v-menu>
        </template>

        <template v-slot:[`item.totalPrice`]="{ item }"> ${{ item.totalPrice }} </template>

        <template v-slot:[`item.financialStatus`]="{ item }">
          <v-chip
            class="ma-2 text-capitalize"
            small
            :color="getFinanceStatus(item.financialStatus).color || 'secondary'"
          >
            <v-icon left small>
              {{ getFinanceStatus(item.financialStatus).icon }}
            </v-icon>
            {{ getFinanceStatus(item.financialStatus).label }}
          </v-chip>
        </template>

        <template v-slot:[`item.fulfillmentStatus`]="{ item }">
          <v-chip class="ma-2" small :color="`${getFulfillStatus(item.fulfillmentStatus)[2]}`">
            <v-icon left small>
              {{ getFulfillStatus(item.fulfillmentStatus)[1] }}
            </v-icon>
            {{ getFulfillStatus(item.fulfillmentStatus)[0] }}
          </v-chip>
        </template>

        <template v-slot:[`item.orderItems`]="{ item }">
          <v-menu :close-on-content-click="false" :max-width="300" max-height="50vh" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-hover v-slot="{ hover }">
                <v-btn plain v-bind="attrs" v-on="on" class="text-none">
                  {{
                    `${(item.lineItems || []).length} ${$tc('item', (item.lineItems || []).length)}`
                  }}
                  <v-icon :style="`opacity:${hover ? 1 : 0}`" right> mdi-chevron-down </v-icon>
                </v-btn>
              </v-hover>
            </template>
            <v-list align="left">
              <v-list-item two-line v-for="(orderItem, index) in item.lineItems || []" :key="index">
                <v-badge
                  offset-x="30"
                  offset-y="30"
                  overlap
                  bordered
                  color="secondary"
                  :content="orderItem.quantity"
                >
                  <v-list-item-avatar tile height="50" width="40">
                    <v-img :src="orderItem.variantImage"></v-img>
                  </v-list-item-avatar>
                </v-badge>
                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    <a target="_blank" :href="`${selectedStore.shopUrl}/products/${orderItem.id}`">
                      {{ orderItem.title }}
                    </a>
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    {{ orderItem.subtitle }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-row style="width: 120px; margin-right: 10px">
            <EngravingGenerator :order="item">
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="engravingTriggerDisabled(item) ? {} : on"
                  class="d-flex align-center engraving-trigger"
                  :class="{ 'engraving-trigger--disabled': engravingTriggerDisabled(item) }"
                >
                  <v-icon :color="engravingTriggerIconColor(item)">mdi-image-edit-outline</v-icon>
                </span>
              </template>
            </EngravingGenerator>

            <email-dialog
              :data="item"
              :store="'MZ'"
              :hideRegistryInfo="true"
              :template="'emails/upload-reminder'"
              :isMizodiaStore="true"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn :color="'orange darken-1'" icon v-bind="attrs" v-on="on">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-on="on" v-bind="attrs">
                        <v-icon> mdi-email-fast-outline </v-icon>
                      </span>
                    </template>
                    <span>{{ $t('Email Reminder') }}</span>
                  </v-tooltip>
                </v-btn>
              </template>
            </email-dialog>

            <v-menu offset-y>
              <template #activator="{ on: onMenu }">
                <v-btn
                  icon
                  v-on="{ ...onMenu }"
                  :disabled="!orderContainsEngraving(item.lineItems || [])"
                >
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <v-icon v-if="!orderContainsEngraving(item.lineItems)">
                          mdi-printer-off
                        </v-icon>
                        <v-badge
                          v-else-if="isAttachmentAvailable(item)"
                          bordered
                          right
                          light
                          overlap
                          style="font-weight: 700"
                          :value="item.metafieldImages?.length"
                          color="blue darken-2"
                          :content="item.metafieldImages?.length || 0"
                          offset-x="0"
                          offset-y="7"
                        >
                          <v-icon color="success"> mdi-printer </v-icon>
                        </v-badge>
                        <v-icon v-else color="error"> mdi-printer-alert </v-icon>
                      </span>
                    </template>
                    <span>{{
                      $t(isAttachmentAvailable(item) ? 'Extract Print' : 'No Files Attached!')
                    }}</span>
                  </v-tooltip>
                </v-btn>
              </template>
              <v-list v-if="getEngravingProducts(item).length > 0">
                <v-list-item
                  link
                  v-for="(product, index) in getEngravingProducts(item)"
                  :key="index"
                  class="text-start"
                >
                  <print-extraction-dialog
                    :title="$t('Order Fulfillment')"
                    :data="item"
                    :store="selectedStore.id"
                    :type="productCategory(product.title)"
                    :image="product.image || item.metafieldImages?.[0] || ''"
                    :fileName="`${item.orderNumber}_${index}_${productCategory(product.title)}`"
                    :lines="product.lines"
                    :font="product.font"
                    :metafieldImages="item.metafieldImages"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on" class="d-flex align-center">
                        <v-img
                          v-if="product.image"
                          :src="product.image"
                          width="50px"
                          style="max-height: 50px"
                        ></v-img>
                        <div
                          v-else
                          style="
                            width: 50px;
                            max-height: 50px;
                            margin-right: 8px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                          "
                        >
                          <v-icon color="error" width="50px"> mdi-alert-circle </v-icon>
                        </div>
                        <v-list-item-title class="ml-2">{{ product.title }}</v-list-item-title>
                      </span>
                    </template>
                  </print-extraction-dialog>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-row>
        </template>

        <template v-slot:footer="{}">
          <v-divider></v-divider>
          <div class="my-2 pa-2">
            <v-btn icon @click="paginate('previous')" :disabled="currentPage <= 0">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn icon @click="paginate('next')">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-sheet>
  </v-container>
</template>

<script>
  import moment from 'moment'
  import customerCard from '../components/CustomerCard'
  import printExtractionDialog from '../components/PrintExtractionDialog.vue'
  import { getMizodiaOrdersShopify } from '../services/shopifyService'
  import { getStoresProtected } from '../services/storeService'
  import { isUserAdmin } from '../services/authService'
  import emailDialog from '../components/EmailDialog'
  import DeprecationBanner from '../components/DeprecationBanner.vue'
  import { EngravingGenerator } from '../components/EngravingGenerator'
  import { getUserUploadImages } from '../components/EngravingGenerator/orderHelpers'
  import { getOtherSvgLineEntries } from '../components/EngravingGenerator/OtherSvgs'
  import {
    getBacksideEngravingLineItems,
    BACKSIDE_ENGRAVING_LINE_ITEM_IDS,
  } from '../components/EngravingGenerator/BacksideEngraving'

  export default {
    data() {
      return {
        stores: [],
        isAdmin: false,
        softwareUrl: 'https://software.space-registry.org/registry/',
        selectedStore: {
          id: 'MIZODIA',
          shopUrl: 'https://mizodia-jewelry.myshopify.com/admin',
        },
        headers: [
          {
            text: 'Order',
            value: 'orderNumber',
            active: true,
            width: '100px',
          },
          { text: 'Date', value: 'createdAt', width: '200px', active: true },
          { text: 'Customer', value: 'customer', active: true },
          { text: 'Total', value: 'totalPrice', active: false, admin: true },
          { text: 'Payment Status', value: 'financialStatus', active: true },
          {
            text: 'Fulfillment Status',
            value: 'fulfillmentStatus',
            active: true,
          },
          { text: 'Items', value: 'orderItems', active: true },
          { text: 'Delivery Method', value: 'delivery', active: false },
          { text: 'Actions', value: 'actions', active: true },
        ],
        filters: [
          { text: 'Show Fulfilled Orders', prop: 'f1' },
          { text: 'Show Unfulfilled Orders', prop: 'f1' },
          { text: 'Show Star Orders', prop: 'f1' },
          { text: 'Show Print Orders', prop: 'f1' },
          { text: 'Show Digital Orders', prop: 'f1' },
          { text: 'Show Paid Orders', prop: 'f1' },
        ],
        orders: [],
        tableRows: [],
        selected: [],
        groupedAs: null,
        searchQuery: '',
        loading: true,
        pages: { next: '', previous: '' },
        currentPage: 0,
        apiUrl: `${process.env.VUE_APP_YOUVENDO_API_SERVER}/`,
      }
    },
    methods: {
      isAttachmentAvailable: function (item) {
        const isAvailable =
          !!(item.lineItems || []).some((lineItem) =>
            (lineItem.properties || []).some(
              ({ name }) =>
                name.includes('File Upload') ||
                (name.includes('File ') &&
                  (name.includes('https://') || name.includes('http://') || name.includes('data:')))
            )
          ) || item.metafieldImages?.length > 0

        return isAvailable
      },
      getPropertyValue: function (lineItem, propertyNames) {
        for (let propertyName in propertyNames) {
          const foundValue =
            (lineItem.properties || []).find(({ name }) => name.includes(propertyName))?.value || ''
          if (foundValue) {
            return foundValue
          }
        }
        return ''
      },
      getEngravingLineItem: function (lineItem) {
        // TODO: refactor the code involving File Upload property.. make a single source of truth for it
        const image = this.getPropertyValue(lineItem, ['File Upload', 'File '])
        const font = this.getPropertyValue(lineItem, ['Schriftart'])
        const lines = [
          this.getPropertyValue(lineItem, ['Zeile 1']),
          this.getPropertyValue(lineItem, ['Zeile 2']),
          this.getPropertyValue(lineItem, ['Zeile 3']),
        ]

        return { title: lineItem.title, image: image ? image : null, font, lines }
      },
      getEngravingProducts: function (item) {
        const lineItems = (item.lineItems || [])
          .filter((lineItem) => this.productCategory(lineItem.title) !== 'other')
          .map((lineItem) => this.getEngravingLineItem(lineItem))
        return lineItems
      },
      fetchOrders: async function () {
        const response = (await getMizodiaOrdersShopify('current', this.selectedStore.id))?.data
        console.log('response::', response)

        this.orders = response?.orders || []
        this.pages = response?.pages
        this.tableRows = [...this.orders]
      },
      async paginate(value) {
        const table = this.$refs.dataTable
        const wrapper = table.$el.querySelector('div.v-data-table__wrapper')

        // Scroll to top of table
        this.$vuetify.goTo(table, { container: wrapper }) // to header of table

        this.loading = true

        const response = (await getMizodiaOrdersShopify(this.pages[value], this.selectedStore.id))
          ?.data
        this.orders = response?.orders || []
        this.pages = response?.pages
        this.tableRows = [...this.orders]

        this.loading = false
        this.currentPage += value === 'previous' ? -1 : 1
      },
      searchTimeOut(e) {
        if (this.timer) {
          clearTimeout(this.timer)
          this.timer = null
        }
        this.timer = setTimeout(async () => {
          await this.searchOrders(e.target.value)
        }, 800)
      },
      searchOrders: async function (keywords) {
        this.loading = true

        const response = (await getMizodiaOrdersShopify('current', this.selectedStore.id, keywords))
          ?.data
        this.orders = response?.orders || []
        this.pages = response?.pages
        this.tableRows = [...this.orders]
        this.loading = false
      },
      orderContainsEngraving(lineItems) {
        return lineItems.some((li) => this.productCategory(li.title) !== 'other')
      },
      orderHasBacksideEngravingLineItems(item) {
        return getBacksideEngravingLineItems(item, BACKSIDE_ENGRAVING_LINE_ITEM_IDS).length > 0
      },
      engravingTriggerDisabled(item) {
        const lineItems = item.lineItems || []
        if (this.orderContainsEngraving(lineItems)) return false
        // Initials / birth data (Geburtsdaten) lines are category "other" but need the modal for Other SVGs
        if (getOtherSvgLineEntries(item).length > 0) return false
        // Backside upsell line items (fixed variant IDs) — same eligibility as inside the modal
        if (this.orderHasBacksideEngravingLineItems(item)) return false
        return true
      },
      engravingTriggerIconColor(item) {
        if (this.engravingTriggerDisabled(item)) return 'grey'
        const lineItems = item.lineItems || []
        const hasMain = this.orderContainsEngraving(lineItems)
        // Green only when main print-type products exist and the customer uploaded images
        if (hasMain && this.orderHasGalleryImages(item)) return 'success'
        // Red: main products without uploads, or enabled only via backside / Other SVGs (no green path)
        return 'error'
      },
      orderHasGalleryImages(item) {
        return getUserUploadImages(item || {}).length > 0
      },
      productCategory(title) {
        title = title.toLowerCase()
        if (title.includes('finger')) {
          return 'fingerprint'
        } else if (title.includes('nasen')) {
          return 'pet_nose_print'
        } else if (title.includes('pfoten') || title.includes('paw')) {
          return 'pet_paw_print'
        } else if (title.includes('handschrift') || title.includes('zeichnung')) {
          return 'handwriting'
        } else if (title.includes('fuß')) {
          return 'baby_feet'
        } else if (title.includes('hand')) {
          return 'baby_hands'
        } else {
          return 'other'
        }
      },
      getFulfillStatus(status) {
        const successIcon = 'mdi-checkbox-marked-circle-outline'
        const failureIcon = 'mdi-checkbox-blank-circle-outline'
        return status === 'fulfilled'
          ? ['Fulfilled', successIcon, 'green darken-4']
          : ['Unfulfilled', failureIcon, 'amber darken-4']
      },
      getFinanceStatus(status) {
        const statuses = {
          paid: {
            icon: 'mdi-checkbox-marked-circle-outline',
            color: 'blue-grey darken-1',
          },
          partially_paid: {
            icon: 'mdi-progress-check',
            color: 'amber darken-3',
          },
          pending: {
            icon: 'mdi-progress-clock',
            color: 'amber darken-3',
          },
          refunded: {
            icon: 'mdi-close-circle-outline',
            color: 'red darken-2',
          },
          partially_refunded: {
            icon: 'mdi-progress-close',
            color: 'amber darken-3',
          },
        }
        return {
          ...(statuses[status.toLowerCase()] || { icon: '' }),
          label: status.replace('_', ' '),
        }
      },
    },
    filters: {
      moment: function (date) {
        return moment(date).calendar(null, {
          lastWeek: 'dddd [at] hh:mm a',
          lastDay: '[Yesterday at] hh:mm a',
          sameDay: '[Today at] hh:mm a',
          nextDay: '[Tomorrow at] hh:mm a',
          nextWeek: '[Next] dddd',
          sameElse: 'MMM D [at] hh:mm a',
        })
      },
    },
    computed: {
      activeHeaders() {
        return this.headers
          .filter((header) => header.active)
          .map((h) => ({ ...h, text: this.$t(h.text) }))
      },
    },
    watch: {
      $route: async function () {
        try {
          this.loading = true
          this.selected = []
          await this.fetchOrders()
        } catch (error) {
          console.log(error)
        } finally {
          this.loading = false
        }
      },
      activeHeaders: function () {
        localStorage.setItem('mizodia-order-headers', JSON.stringify(this.headers))
      },

      groupedAs: function () {
        if (this.groupedAs === null) return localStorage.removeItem('mizodia-order-group')
        localStorage.setItem('mizodia-order-group', this.groupedAs)
      },
    },
    mounted: async function () {
      this.isAdmin = await isUserAdmin()
      this.stores = await getStoresProtected()
      const headers = localStorage.getItem('mizodia-order-headers')
      if (headers) this.headers = JSON.parse(headers)
      this.groupedAs = localStorage.getItem('mizodia-order-group') || null

      try {
        await this.fetchOrders()
      } catch (error) {
        console.log(error)
      }

      this.loading = false
    },
    components: {
      customerCard,
      printExtractionDialog,
      emailDialog,
      EngravingGenerator,
      DeprecationBanner,
    },
  }
</script>

<style scoped>
  .fixed-col {
    position: sticky;
    left: 0;
    background-color: #1c1d1e;
    transition: background-color 0.2s;
    /* Add a smooth transition effect */
  }

  /* Change background color on column hover */
  .fixed-col:hover,
  .v-data-table tbody tr:hover .fixed-col {
    background-color: #5c5c5d;
    /* Change to the desired hover color */
  }

  /* Change background color on row hover */
  .v-data-table tbody tr:hover {
    background-color: #5c5c5d;
    /* Change to the desired hover color */
  }

  .engraving-trigger--disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
</style>
